import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';

export type Notification = components['schemas']['NotificationDto'];
export type NotificationSummary = components['schemas']['NotificationSummaryDto'];

export interface NotificationPage {
  readonly data: Notification[];
  readonly next_cursor: string | null;
}

const PAGE_SIZE = 50;

/** A generic list or action failure (network or an unexpected status); shown as `ui-error-state`. */
export class NotificationsLoadError extends Error {
  constructor(readonly status: number) {
    super(`notifications request answered ${status}`);
  }
}

/** The signed-in member's own notifications of the active tenant, newest first (spec 50 §5, AC-50-024). */
export async function fetchNotificationsPage(cursor: string | undefined, unreadOnly: boolean): Promise<NotificationPage> {
  let result;
  try {
    result = await api.GET('/v1/notifications', { params: { query: { limit: PAGE_SIZE, cursor, unread: unreadOnly || undefined } } });
  } catch {
    throw new NotificationsLoadError(0);
  }
  if (result.response.ok && result.data) return result.data;
  throw new NotificationsLoadError(result.response.status);
}

export async function fetchNotificationSummary(): Promise<NotificationSummary> {
  let result;
  try {
    result = await api.GET('/v1/notifications/summary');
  } catch {
    throw new NotificationsLoadError(0);
  }
  if (result.response.ok && result.data) return result.data;
  throw new NotificationsLoadError(result.response.status);
}

export async function markNotificationRead(id: string): Promise<Notification> {
  let result;
  try {
    result = await api.POST('/v1/notifications/{id}/read', { params: { path: { id } } });
  } catch {
    throw new NotificationsLoadError(0);
  }
  if (result.response.ok && result.data) return result.data;
  throw new NotificationsLoadError(result.response.status);
}

export async function markAllNotificationsRead(): Promise<void> {
  let result;
  try {
    result = await api.POST('/v1/notifications/read-all');
  } catch {
    throw new NotificationsLoadError(0);
  }
  if (!result.response.ok) throw new NotificationsLoadError(result.response.status);
}
